/**
 * Daily Transfer Out Service
 * Frontend API wrapper for the daily outbound transfer document.
 * One daily transfer out per production day, aggregating finished products,
 * rejected materials, and remaining raw materials.
 */
import api from './api';

// ============================================================================
// Types
// ============================================================================

// WCO-2618: one label print/reprint event for the (i) print-audit popover.
// status: 'active' = current label; 'discarded' = superseded by a reprint; 'voided' = invalidated.
export interface PrintHistoryEntry {
  barcode: string | null;
  printed_weight: number | null;
  print_number: number;
  printed_at: string;
  printed_by: string | null;      // resolved display name (falls back to employee number)
  printer_name?: string | null;
  status: 'active' | 'discarded' | 'voided';
  discarded_at?: string | null;
  discard_reason?: string | null;
}

export interface DailyTransferOutItem {
  id: number;
  daily_transfer_out_id: number;
  transfer_detail_id?: number;
  category: 'finished_product' | 'finished_product_not_intact' | 'rejected_material' | 'remaining_material' | 'by_product' | 'by_product_marketable' | 'by_product_production';
  item_number: string;
  item_name?: string;
  item_chinese_name?: string;
  english_brand_name?: string;
  packing?: string;
  unit_weight?: number;
  expected_quantity?: number;
  expected_weight?: number;
  actual_quantity?: number;
  actual_weight?: number;
  yield_factor?: number;
  actual_yield_percentage?: number;
  yield_variance?: number;
  raw_input_weight?: number;
  source_session_number?: string;
  source_lot_id?: string;
  source_material_item_number?: string;
  remaining_type?: 'unused_lot' | 'remainder_intact' | 'remainder_not_intact';
  reserved_weight?: number;
  notes?: string;
  has_label?: boolean;
  // WCO-2618: full label print/reprint/supersede history for FP-NI / RM-NI rows (from
  // ics.hte_lot_label_print_history, WCO-2581). Drives the per-row (i) print-audit popover so the
  // supervisor can see why "Print Labels" is disabled. Empty [] when no lot label history; absent on
  // rows that don't carry a lot label (e.g. by-products — a follow-up).
  print_history?: PrintHistoryEntry[];
  // WCO-2618: per-variant printed state for marketable by-products (the Cases/Buffer split rows).
  // cases_printed ⇐ a marketable case has printed_at set (not voided); buffer_printed ⇐ buffer status='printed'.
  // NI/remaining rows use has_label instead; these are only populated for by_product_marketable.
  cases_printed?: boolean;
  buffer_printed?: boolean;
  // WCO-2618: single-entry print history per by-product variant (case / buffer) — printed_at +
  // resolved printed_by from hte_byproduct_marketable_case / _buffer. The frontend picks the one
  // matching each split card's _variant to drive the (i) popover. Empty [] when that variant is unprinted.
  print_history_cases?: PrintHistoryEntry[];
  print_history_buffer?: PrintHistoryEntry[];
  // WCO-2322: derived per-row shipped state for finished products ('shipped' once all its outputs are sent).
  ship_status?: 'shipped' | null;
  excluded?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DailyTransferOutTotals {
  finished: {
    expected_quantity: number;
    expected_weight: number;
    actual_quantity: number;
    actual_weight: number;
  };
  finished_not_intact: {
    count: number;
    total_weight: number;
  };
  rejected: {
    count: number;
    total_weight: number;
  };
  remaining: {
    count: number;
    total_weight: number;
  };
  // WCO-2606: gross remainder produced on this day (frozen actual_weight, reconciles with Yield Variance).
  // Distinct from `remaining` (live net on-hand). Optional for backward-compat with cached/older responses.
  remainder_produced_today?: {
    count: number;
    total_weight: number;
  };
  by_products: {
    count: number;
    total_weight: number;
  };
  raw_materials: {
    expected_weight: number;
    received_weight: number;
    rejected_weight: number;
    on_hold_weight: number;
  };
}

// WCO-2412: one feeding session's contribution to an output — planned cases, post-cascade target
// (cases to send), and the delta (+surplus / −deficit). Drives the self-reconcilable group counter
// (WCO-2413) and the Select-Output derive-the-target tooltip (WCO-2414).
export interface OutputSessionBreakdown {
  session_number: string;
  item_number: string | null;       // the session's product_sku
  item_english_name: string | null;
  planned: number;                  // manager plan (expected_cases)
  target: number;                   // post-cascade cases to send into this output
  delta: number;                    // target − planned (>0 surplus, <0 deficit, 0 on-plan)
}

// WCO-2322 (Slice 5.7 → 6D): per-destination output reconciliation + scan completeness.
export interface OutputReconciliation {
  output_number: string;
  destination_warehouse: string;
  status: 'active' | 'cancelled';
  // WCO-2433: the output's own sent→received lifecycle. 'draft' = not sent; 'pending' = sent, awaiting
  // warehouse; 'complete' = warehouse received. Sent outputs (!== 'draft') stay in the modal, disabled + badged.
  ship_status?: 'draft' | 'pending' | 'complete';
  is_ad_hoc?: boolean;          // supervisor-created (no feeding session) → deletable
  has_document?: boolean;       // WCO-2655: has a renderable ICS production record (production_summary) yet
  expected_cases: number;       // raw manager plan
  suggested_target?: number;    // primary-cascade target that absorbs surplus/deficit (the denominator)
  session_breakdown?: OutputSessionBreakdown[]; // WCO-2412: per-session {planned, target, delta}
  actual_cases: number;         // LIVE finished-scan count
  extra_cases?: number;         // LIVE FP-NI / by-product scans (extras, not counted toward expected)
  variance: number; // actual - expected
}

export interface UnscannedBox {
  barcode_number: string;
  item_number: string;
  box_number: string | null;
  item_name: string;
}

// WCO-2322 (6B): a materialized raw-material (warehouse) transfer for the day (the on-demand `+ Transfer` ones).
export interface RawMaterialTransferRow {
  transfer_number: string;
  destination_warehouse: string;
  total_items: number;
  scanned_count: number;
  status?: string;   // WCO-2650: workflow status — sent transfers stay visible, rendered disabled + "Sent"
}

// WCO-2322 (Confirm): one not-yet-sent container in the day-wide Confirm review.
export interface ConfirmContainerItem {
  id: string;
  barcode: string;
  item_number: string;
  item_english_name?: string;
  actual_weight?: number | string | null;
  source?: string | null;
}
export interface ConfirmContainer {
  type: 'output' | 'transfer';
  number: string;
  destination: string;
  expected: number;
  scanned: number;
  extra: number;
  complete: boolean;
  // WCO-2433: a SENT output shown in the review for context (disabled, no Send button). Transfers are never
  // marked sent here (draft-only). ship_status carries the sent→received stage for the "Sent" badge wording.
  sent?: boolean;
  ship_status?: 'draft' | 'pending' | 'complete';
  items: ConfirmContainerItem[];
}

export interface DailyTransferOut {
  id: number;
  production_date: string;
  transfer_number: string;
  transfer_status?: string;
  status: 'draft' | 'pending' | 'complete';
  expected_generated_at?: string;
  actuals_generated_at?: string;
  finalized_by?: string;
  finalized_at?: string;
  created_at: string;
  finished_products: DailyTransferOutItem[];
  finished_products_not_intact: DailyTransferOutItem[];
  rejected_materials: DailyTransferOutItem[];
  remaining_materials: DailyTransferOutItem[];
  by_products: DailyTransferOutItem[];
  totals: DailyTransferOutTotals;
  // WCO-2322 (6D)
  outputs?: OutputReconciliation[];
  unscanned_count?: number;
  unscanned_boxes?: UnscannedBox[];
  // WCO-2322 (6B): the day's materialized raw-material transfers (for the scan modal's Transfer rows)
  raw_material_transfers?: RawMaterialTransferRow[];
  // WCO-2322: every day transfer with its OWN workflow status (for the header's per-transfer capsule)
  day_transfers?: Array<{ transfer_number: string; destination_warehouse: string; status: string; has_document?: boolean }>;
}

export interface AggregateActualsResponse {
  daily_transfer_out_id: number;
  transfer_number: string;
  production_date: string;
  summary: {
    finished_products: number;
    rejected_materials: number;
    remaining_materials: number;
    by_products: number;
  };
}

// ============================================================================
// Service
// ============================================================================

const BASE = '/inventory/daily-transfer-out';

export const dailyTransferOutService = {
  async getDailyTransferOut(date: string): Promise<DailyTransferOut> {
    let response;
    try {
      response = await api.get(`${BASE}/${date}`);
    } catch (err: any) {
      if (err?.response?.status === 404) return undefined as any;
      throw err;
    }
    const data: DailyTransferOut = response.data.data;
    // Normalize yield values from whole % to decimal on finished product items
    if (data.finished_products) {
      for (const item of data.finished_products) {
        if (item.yield_factor != null) item.yield_factor = Number(item.yield_factor) / 100;
        if (item.actual_yield_percentage != null) item.actual_yield_percentage = Number(item.actual_yield_percentage) / 100;
        if (item.yield_variance != null) item.yield_variance = Number(item.yield_variance);
      }
    }
    return data;
  },

  async aggregateActuals(date: string): Promise<AggregateActualsResponse> {
    const response = await api.post(`${BASE}/aggregate-actuals`, {
      production_date: date,
    });
    return response.data.data;
  },

  async submitTransferOut(date: string, submitted_by?: string): Promise<void> {
    await api.patch(`${BASE}/${date}/submit`, { submitted_by });
  },

  // WCO-2322 (Step 2): create an EMPTY container to fill by scanning — an ad-hoc Output or a raw-material
  // Transfer, for a chosen destination. Idempotent per (date, destination).
  async createAdHocOutput(date: string, destination: string, createdBy?: string): Promise<{ output_number: string }> {
    const res = await api.post(`${BASE}/${date}/new-output`, { destination, created_by: createdBy });
    return res.data?.data ?? res.data;
  },
  async createNewTransfer(date: string, destination: string, createdBy?: string): Promise<{ transfer_number: string }> {
    const res = await api.post(`${BASE}/${date}/new-transfer`, { destination, created_by: createdBy });
    return res.data?.data ?? res.data;
  },
  // WCO-2322 (delete): remove a not-yet-sent supervisor transfer / ad-hoc output (frees its number for reuse).
  async deleteTransfer(transferNumber: string): Promise<void> {
    await api.delete(`${BASE}/transfer/${transferNumber}`);
  },
  async deleteAdHocOutput(outputNumber: string): Promise<void> {
    await api.delete(`${BASE}/output/${outputNumber}`);
  },

  // WCO-2322 (Step 3): scan a case into a raw-material transfer (staging). Two kinds:
  //  - our remainder/rejected LABEL barcode → omit `resolved` (backend matches label_barcode_number).
  //  - an intact supplier box → the caller parses it with parseGS1128Barcode + lookupGtin (SAME as transfer-in
  //    receiving) and passes the resolved { itemNumber, weight } so no barcode parsing happens server-side.
  async scanRawMaterialToTransfer(
    transferNumber: string, barcode: string, scannedBy: string,
    resolved?: { itemNumber: string; weight: number }
  ): Promise<{ item_number: string }> {
    const res = await api.post(`${BASE}/raw-transfer-scan/${transferNumber}`, {
      barcode, scanned_by: scannedBy,
      ...(resolved ? { item_number: resolved.itemNumber, weight: resolved.weight } : {}),
    });
    return res.data?.data ?? res.data;
  },
  // WCO-2545: the transfer's still-available damaged (MANUAL) cases — captured/weighed at waste tracking,
  // minus those already added. The Scan Cases modal lists these as click-to-add rows.
  async getManualCasesForTransfer(transferNumber: string): Promise<{ manualCases: Array<{ dto_item_id: string; item_number: string; item_english_name?: string; weight: number }> }> {
    const res = await api.get(`${BASE}/raw-transfer-scan/${transferNumber}/manual-cases`);
    return res.data?.data ?? res.data ?? { manualCases: [] };
  },
  // WCO-2545: manually add ONE damaged/unreadable intact case to the transfer (no barcode to scan).
  // Caps against the item's pooled MANUAL bucket (damaged cases logged at waste tracking). Weight is
  // required — the transfer weight gate must reconcile.
  // WCO-2599: pass the SELECTED dto_item_id so the backend consumes the exact remainder the supervisor
  // clicked. Without it the backend resolved by item_number + LIMIT 1, which is ambiguous (and could
  // ship the wrong lot) when an item has multiple untransferred intact-remainder rows that day.
  async addManualRawMaterialToTransfer(
    transferNumber: string, itemNumber: string, weight: number, scannedBy: string, dtoItemId?: string
  ): Promise<{ item_number: string }> {
    const res = await api.post(`${BASE}/raw-transfer-scan/${transferNumber}`, {
      manual: true, item_number: itemNumber, weight, scanned_by: scannedBy,
      // WCO-2599 (copilot #3): only send dto_item_id when the caller actually provides one, so the body
      // never carries a `dto_item_id: undefined` property for legacy callers.
      ...(dtoItemId ? { dto_item_id: dtoItemId } : {}),
    });
    return res.data?.data ?? res.data;
  },
  async undoRawMaterialTransferScan(transferNumber: string, scanId: string): Promise<void> {
    await api.delete(`${BASE}/raw-transfer-scan/${transferNumber}/${scanId}`);
  },
  // Resolved scans for a raw-material transfer (item number, English name, weight) for the Scan Cases list.
  async getRawMaterialTransferScans(transferNumber: string): Promise<{ scans: any[] }> {
    const res = await api.get(`${BASE}/raw-transfer-scan/${transferNumber}`);
    return res.data?.data ?? res.data ?? { scans: [] };
  },

  // WCO-2322 (Confirm): day-wide review summary — every not-yet-sent container + its items + complete flag.
  async getConfirmSummary(date: string): Promise<{ containers: ConfirmContainer[]; all_complete: boolean }> {
    const res = await api.get(`${BASE}/${date}/confirm-summary`);
    return res.data?.data ?? { containers: [], all_complete: false };
  },

  // WCO-2322 (Confirm) + WCO-2433 (partial send): commit not-yet-sent containers (production_*/sales_*/
  // warehouse_transfer_* + lot deductions). Omit `containerNumbers` to send ALL (day-wide gate). Pass a
  // subset to send only those, each gated on its OWN completeness (409 per short container).
  async confirmDay(date: string, confirmedBy?: string, containerNumbers?: string[]): Promise<{ confirmed: Array<{ type: string; number: string }> }> {
    const res = await api.post(`${BASE}/${date}/confirm`, { confirmed_by: confirmedBy, container_numbers: containerNumbers });
    return res.data?.data ?? res.data;
  },

  // WCO-2322 (6C): create an on-demand RAW-MATERIAL transfer back (remaining + rejected) for the day.
  // Optional item_ids restricts it to a selected subset; omit to transfer all untransferred not-processed items.
  async createRawMaterialTransfer(
    date: string,
    submitted_by?: string,
    item_ids?: number[]
  ): Promise<{ transfer_number: string; detail_count: number }> {
    const res = await api.post(`${BASE}/${date}/raw-material-transfer`, { submitted_by, item_ids });
    return res.data?.data ?? res.data;
  },

  async finalizeTransferOut(
    date: string,
    finalized_by?: string,
    adjustments?: Array<{ item_id: number; actual_quantity?: number; actual_weight?: number }>
  ): Promise<void> {
    await api.patch(`${BASE}/${date}/finalize`, {
      finalized_by,
      adjustments,
    });
  },

  async initializeTransferOut(
    production_date: string,
    received_items: Array<{ item_number: string; accepted_weight: number; accepted_cases?: number }>
  ): Promise<{ daily_transfer_out_id: number; transfer_number: string; items: any[] }> {
    const response = await api.post(`${BASE}/initialize`, {
      production_date,
      received_items,
    });
    return response.data.data;
  },

  async toggleByProductExclude(itemId: number, excluded: boolean): Promise<void> {
    await api.patch(`${BASE}/items/${itemId}/exclude`, { excluded });
  },

  async createByProductInventory(
    production_date: string,
    // WCO-2633 Phase 2: a force reprint targets a specific variant ('cases' | 'buffer') of the item.
    items: Array<{ item_id: number; variant?: 'cases' | 'buffer' }>,
    operator_id?: string,
    // WCO-2633: force = supervisor Reprint override for an already-printed by-product label; reason is
    // stamped on the superseded unified history row.
    opts?: { force?: boolean; reason?: string }
  ): Promise<{ results: any[]; simulate: boolean }> {
    const response = await api.post(`${BASE}/create-byproduct-inventory`, {
      production_date,
      items,
      operator_id,
      force: opts?.force,
      reason: opts?.reason,
    });
    return response.data.data;
  },

  // Not-intact finished products are keyed by lot id (UUID), not daily_transfer_out_item id.
  async createFinishProductNiLabels(
    production_date: string,
    items: Array<{ item_id: string | number }>,
    operator_id?: string,
    // WCO-2623: force = supervisor Reprint override (reprint an already-current label, supersede the
    // prior one); reason is stamped on the superseded history row.
    opts?: { force?: boolean; reason?: string }
  ): Promise<{ results: any[]; simulate: boolean }> {
    const response = await api.post(`${BASE}/create-finish-product-ni-labels`, {
      production_date,
      items,
      operator_id,
      force: opts?.force,
      reason: opts?.reason,
    });
    return response.data.data;
  },

  // Remainder (not intact) raw material labels. item_id = daily_transfer_out_item.id;
  // backend resolves source_lot_id and tracks printed via ics.hte_lot.label_printed.
  async createRemainderNiLabels(
    production_date: string,
    items: Array<{ item_id: string | number }>,
    operator_id?: string,
    // WCO-2623: force = supervisor Reprint override (see createFinishProductNiLabels).
    opts?: { force?: boolean; reason?: string }
  ): Promise<{ results: any[]; simulate: boolean }> {
    const response = await api.post(`${BASE}/create-remainder-ni-labels`, {
      production_date,
      items,
      operator_id,
      force: opts?.force,
      reason: opts?.reason,
    });
    return response.data.data;
  },
};

export default dailyTransferOutService;
